/**
 * Pinned processing result for the Smiley Face practice design.
 *
 * Colour detection runs through Gemini, which returns its palette in no
 * particular order, and that order becomes the layer order in the Stitch
 * Editor. The walkthrough says "click layer 2 — the black", so a reshuffle
 * sends the learner to the wrong swatch. Tutorial designs therefore skip
 * detection and use a fixed palette instead.
 *
 * Values were sampled from the asset itself (see scripts/sampleTutorialColors.js):
 * yellow 72.0% of the opaque pixels, black 27.5%. The order below is also a
 * sensible stitch order — the face first, the black on top of it.
 */
export const palette = [
  { hex: 'ffe600', label: 'yellow face' },
  { hex: '000000', label: 'black outline, eyes and mouth' },
];

/** Shape `processAndAnalyzeImage` returns: `[hexWithoutHash, enabled]`. */
export const colors = palette.map(({ hex }) => [hex, true]);

/**
 * One-based layer numbers, matching the "Select layer N" controls.
 * The lesson's copy and its step targets are both built from these, so the two
 * can't drift apart if the palette order is ever changed.
 */
export const layers = {
  face: 1,
  features: 2,
};

/**
 * Pinned stitch-settings analysis.
 *
 * Normally a model picks a starting stitch type per layer, which makes the
 * lesson different every time — and if it happened to choose satin for the
 * black, the walkthrough's "Replace this layer" step would have nothing to
 * replace. Both layers therefore start as fills, which is what makes the
 * conversion to satin an actual change the learner performs.
 *
 * `index` is zero-based and lines up with `palette`.
 */
export const aiAnalysis = {
  layers: [
    {
      index: 0,
      description: 'The round yellow face',
      recommended_stitch_type: 'auto_fill',
      angle: 0,
      row_spacing_mm: 0.4,
      max_stitch_length_mm: 2.8,
      fill_underlay: true,
      fill_underlay_inset_mm: 0.3,
      pull_compensation_mm: 0.25,
      expand_mm: 0.16,
      staggers: 4,
      bean_stitch_repeats: 0,
      randomize_stitch_length: true,
      reason: 'One large rounded area stitches cleanly as a standard fill.',
    },
    {
      index: 1,
      description: 'Black ring, two eyes and a smile',
      recommended_stitch_type: 'auto_fill',
      angle: 0,
      row_spacing_mm: 0.35,
      max_stitch_length_mm: 2.6,
      fill_underlay: true,
      fill_underlay_inset_mm: 0.3,
      pull_compensation_mm: 0.25,
      expand_mm: 0.16,
      staggers: 4,
      bean_stitch_repeats: 0,
      randomize_stitch_length: true,
      reason: 'Starts as a fill — the walkthrough converts these bands to satin.',
    },
  ],
  global: {
    suggested_density: 'normal',
    estimated_complexity: 'simple',
    warnings: [],
    tips: [
      'Stitch the face first, then the black features on top.',
      'Grow the yellow slightly so no fabric shows around the black.',
    ],
  },
  machine_recommendations: {
    stabilizer: {
      type: 'cut-away',
      weight: 'medium',
      reason: 'A broad filled circle pulls the fabric and needs stable backing.',
    },
    needle: {
      type: 'universal',
      size: '75/11',
      reason: 'Suits standard 40wt rayon or polyester thread.',
    },
    tension: { top: 'normal', bobbin: 'normal', notes: 'Standard settings are fine for this design.' },
    speed: 'medium',
    hooping: 'Hoop the fabric and stabiliser together, drum-tight.',
  },
};

const fixture = { palette, colors, layers, aiAnalysis };

export default fixture;
